import React from "react";
import styles from "./ProductDetails.module.css";
import { toProductHref, linkTarget } from "../../utils/url";

interface Spec {
    name: string;
    value: string;
}

interface Pricing {
    hasSalePrice?: boolean;
    salePrice?: number;
    listPrice?: number;
    web?: {
        listPrice?: number;
        salePrice?: number;
        hasSalePrice?: boolean;
    };
    store?: {
        listPrice?: number;
        salePrice?: number;
        hasSalePrice?: boolean;
        netListPrice?: number;
        netSalePrice?: number;
    };
    priceSource?: "web" | "store";
}

interface ProductDetailsProps {
    details: {
        brand: string;
        content: string;
        description: string;
        pricing: Pricing;
        specs: Spec[];
        sourcepage: string;
        price_source?: "web" | "store";
        store_list_price?: number;
        store_sale_price?: number;
        store_price?: number;
        storePrice?: number;
    } | null;
    priceSource?: "web" | "store";
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ details, priceSource }) => {
    if (!details) return <p>No details available.</p>;

    const { brand, content, description, pricing, specs, sourcepage, price_source, store_list_price, store_sale_price, store_price, storePrice } = details;

    const formatPrice = (price: number) => new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(price);
    const toNumber = (value: any): number | undefined => {
        if (typeof value === "number") return value;
        if (typeof value === "string" && value.trim()) {
            const parsed = Number(value);
            return isNaN(parsed) ? undefined : parsed;
        }
        return undefined;
    };

    const pricingData = (pricing ?? {}) as Pricing;
    const resolvedSource = priceSource || pricingData.priceSource || price_source || "web";
    const webPricing = pricingData.web || {
        listPrice: pricingData.listPrice,
        salePrice: pricingData.salePrice,
        hasSalePrice: pricingData.hasSalePrice
    };
    const storePricing = pricingData.store || {
        listPrice: store_list_price,
        salePrice: store_sale_price ?? store_price ?? storePrice,
        hasSalePrice: pricingData.hasSalePrice
    };

    const activeSource = resolvedSource === "store" ? "store" : "web";
    const activePricing = activeSource === "store" ? storePricing : webPricing;
    const activeListPrice = toNumber(activePricing.listPrice);
    const activeSalePrice = toNumber(activePricing.salePrice);
    const hasSalePrice = activePricing.hasSalePrice ?? (activeSalePrice !== undefined && activeListPrice !== undefined && activeSalePrice < activeListPrice);
    const displayPrice = hasSalePrice && activeSalePrice !== undefined ? activeSalePrice : activeListPrice;
    const crossedPrice = hasSalePrice && activeListPrice ? activeListPrice : undefined;

    return (
        <div className={styles.detailsContainer}>
            <h1>{content}</h1>
            {(() => {
                const href = toProductHref(sourcepage);
                const target = linkTarget();
                return (
                    <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined}>
                        Open in Public.gr
                    </a>
                );
            })()}
            <h2>Brand: {brand}</h2>
            <h3>
                {displayPrice !== undefined ? (
                    crossedPrice !== undefined ? (
                        <>
                            Αρχική: <span style={{ textDecoration: "line-through" }}>{formatPrice(crossedPrice)}</span> - Τελική: {formatPrice(displayPrice)}
                        </>
                    ) : (
                        <>Τιμή: {formatPrice(displayPrice)}</>
                    )
                ) : (
                    <>Τιμή: —</>
                )}
            </h3>
            <div>
                <h4>Χαρακτηριστικά:</h4>
                <ul className={styles.specList}>
                    {specs.map((spec, index) => (
                        <li key={index} className={styles.specItem}>
                            {spec.name}: {spec.value}
                        </li>
                    ))}
                </ul>
            </div>
            <div dangerouslySetInnerHTML={{ __html: description }} />
        </div>
    );
};

export default ProductDetails;
